import { Box, Divider, Grid, Typography } from '@mui/material'
import TableRowExport from 'component/Pdf/TableRowExport'
import TableCustom from 'component/Table/TableCustom'
import { colors } from 'constants/theme'
import { ExportProps } from 'features/report/report-constants'
import _ from 'lodash'
import moment from 'moment'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'

/** TRANSLATION*/
import { useTranslation } from 'react-i18next'

/** API */
import ClinicApi from 'api/master/clinic.api'
import ReportApi, { FindAllReportInterface } from 'api/report.api'
import OperativeTypeApi from 'api/setting/treatments/operative-type.api'

/** COMPONENT */
import HeaderCard from 'component/CardCustom/HeaderCard'
import InputSecondNewRangePicker, { emptyRangePicker } from 'component/Input/InputSecondNewRangePicker'
import Loading from 'component/Loading'
import AutocompleteSelect from 'component/Select/AutocompleteSelect'
import TableRowCommon from 'component/Table/TableRowCommon'

/** REPORT */
import OperativeList from './OperativeList'

/** CONSTANTS */
import { useDownloadExcel } from 'component/Excel/hooks/useExcel'
import { getBranch, numberFormat, toBuddhistYear } from 'utils/app.utils'

/** STYLE */
import OperativeApi from 'api/setting/treatments/operative.api'
import { handleChangeFilterSelect } from 'features/dashboard/dashboard'
import * as UseStyled from 'features/report/useStyled'
import DropdownButton from 'new-components/buttons/dropdown-button'
import MainButton from 'new-components/buttons/main-button'
import SelectInput from 'new-components/inputs/select-input'
import SelectCheckBoxInput from 'new-components/inputs/select_checkbox_input'
import { routeName } from 'routes/routes-name'
import ExportToExcel from './ExportToExcel'

interface OperativeTypeInterface {
  operativeTypeId: number
  operativeTypeName: string
}

export default function OperativeReport() {
  const { t } = useTranslation()
  const history = useHistory()
  const componentRef = useRef<HTMLDivElement>(null)
  const componentRefPDF = useRef<HTMLDivElement>(null)
  const momentNowRange = [moment().startOf('month').format('YYYY-MM-DD'), moment().format('YYYY-MM-DD')]
  const momentDate = () => momentNowRange

  const [loading, setLoading] = useState<boolean>(true)
  const [branches, setBranches] = useState<any[]>([])

  const [branchId, setBranchId] = useState<any>(getBranch())
  const [rangeDate, setRangeDate] = useState<string[]>(momentDate)
  const [operativeTypes, setOperativeTypes] = useState<any>([])
  const [operative, setOperative] = useState<any[]>([])
  const [operativeTypeId, setOperativeTypeId] = useState<string>('ALL')
  const [operativeId, setOperativeId] = useState<string>('')
  const [selectOperative, setSelectOperative] = useState<any[]>(['ALL'])

  const [billStatus, setBillStatus] = useState<string>('ALL')
  const [operativePage, setOperativePage] = useState<number>(1)
  const [operativePageLimit, setOperativePageLimit] = useState<number>(10)
  const [operativeRowCount, setOperativeRowCount] = useState<number>(0)
  const [operativeSortBy, setOperativeSortBy] = useState<string>('createAt')
  const [operativeSortType, setOperativeSortType] = useState<string>('ASC')
  const [orderedSelectOperativesType, setOrderedSelectOperativesType] = useState<any[]>([])
  const [operativeTypeMultiple, setOperativeTypeMultiple] = useState<OperativeTypeInterface[]>([])
  const [operativeData, setOperativeData] = useState<any[]>([])
  const [operativeDataSum, setOperativeDataSum] = useState<any[]>([])
  const [item, setItem] = useState<(string | number)[]>([])
  const [operativeExport, setOperativeExportData] = useState<any[]>([])
  const [sumOperativeExportData, setSumOperativeExportData] = useState<any[]>([])
  const [fetch, setFetch] = useState<boolean>(false)

  const [isOpenListItem, setIsOpenListItem] = useState(false)

  const handleFilterItem = (value: any) => {
    if ((value === 'ALL' && _.size(item.filter((item) => typeof item === 'number')) === _.size(operative)) || value === 'CLEAR') {
      setItem([''])
    } else if (value === 'ALL') {
      setItem(['ALL', ..._.map(operative, (d: any) => d.operativeId)])
    } else if (_.includes(item, value)) {
      setItem(_.filter(item, (d) => d !== value && d !== 'ALL'))
    } else {
      setItem([...item, value])
    }
  }

  const verifyPermission = async () => {
    const condition: FindAllReportInterface = {
      page: 1,
      pageLimit: 200
    }
    ReportApi.reportList(condition).then(({ data }) => {
      const check = _.find(data, (val: any) => String(val.reportKey) === 'OPERATIVE_SUMMARY_MONTH') ? true : false
      if (!check) {
        history.push(routeName.report)
        return false
      }
    })

    /** MASTER DATA */
    ClinicApi.findAllBranches()
      .then(({ data }) => setBranches(data))
      .catch((e) => {
        return
      })

    OperativeTypeApi.findAll({ status: 'ACTIVE', pageLimit: 50 })
      .then(({ data }) => {
        setOperativeTypes(data)
      })
      .catch((e) => {
        return
      })

    return true
  }

  useEffect(() => {
    verifyPermission()
  }, [])

  useEffect(() => {
    let operativeCondition: any = { status: 'ACTIVE', pageLimit: 50 }
    if (operativeTypeId) operativeCondition = { ...operativeCondition, operativeTypeId: operativeTypeId }

    OperativeApi.findAll(operativeCondition)
      .then(({ data }) => {
        setOperative(data)
        setItem(['ALL', ...data.map((d: any) => d.operativeId)])
      })
      .catch((e) => {
        return
      })
  }, [operativeTypeId])

  const fetchListOperative = (value: string) => {
    OperativeApi.findAll({
      status: 'ACTIVE',
      pageLimit: 50,
      operativeTypeId: operativeTypeId,
      search: value
    }).then(({ data }) => {
      setOperative(data)
    })
      .catch((e) => {
        return
      })
  }

  /** TABLE 1 */
  const fetchOperative = useCallback(() => {
    let condition: any = {
      page: operativePage,
      pageLimit: operativePageLimit,
      branchId,
      billStatus
    }
    if (operativeTypeId !== 'ALL') condition = { ...condition, operativeType: operativeTypeId }
    if (item.length) {
      const listItem = _.filter(item, (v: any) => v > 0).join(',')
      if (item[0] !== "ALL" && listItem) condition = { ...condition, operativeId: listItem }
    }

    if (operativeSortType) condition = { ...condition, sortType: operativeSortType }
    if (operativeSortBy) condition = { ...condition, sortBy: operativeSortBy }

    if (rangeDate[0] !== null && rangeDate[1] !== null) condition = { ...condition, dateStart: rangeDate[0], dateEnd: rangeDate[1] }
    ReportApi.reportOperative(condition)
      .then(({ headers, data }) => {
        setOperativeData(data.rows)
        setOperativeDataSum(data.sumOperative)
        setOperativeRowCount(headers['x-total'])
      })
      .catch((e) => setLoading(false))
      .finally(() => setLoading(false))
    setFetch(false)
  }, [operativePage, operativePageLimit, operativeSortType, operativeSortBy, fetch])

  const onChangeOperativeSort = (sortByVal: string, sortTypeVal: string) => {
    setOperativeSortType(sortTypeVal)
    setOperativeSortBy(sortByVal)
  }

  const onChangeOperativeRowPerPage = (limit: number) => setOperativePageLimit(limit)
  const onChangeOperativePage = (val: number) => setOperativePage(val)

  const loadOperativeExport = useCallback(async () => {
    let condition: any = {
      page: 1,
      pageLimit: 1000,
      branchId,
      billStatus,
      export: 1
    }

    if (operativeTypeId !== 'ALL') condition = { ...condition, operativeType: operativeTypeId }
    if (item.length) {
      const listItem = _.filter(item, (v: any) => v > 0).join(',')
      if (item[0] !== "ALL" && listItem) condition = { ...condition, operativeId: listItem }
    }

    if (rangeDate[0] !== null && rangeDate[1] !== null) condition = { ...condition, dateStart: rangeDate[0], dateEnd: rangeDate[1] }

    ReportApi.reportOperative(condition)
      .then(({ data }) => {
        setOperativeExportData(data.rows)
        setSumOperativeExportData(data.sumOperative)
      })
      .catch((e) => setLoading(false))
      .finally(() => setLoading(false))
    setFetch(false)
  }, [fetch])

  const exportExcel = async () => {
    setLoading(true);
    try {
      const opType = _.find(operativeTypes, (operativeTypesData: any) => operativeTypesData.operativeTypeId === operativeTypeId)
      const operativeTypeName = opType?.operativeTypeName || t(`REPORT.FILTER.ALL`)
      const operativeName = item[0] !== 'ALL'
        ? operative
          .filter((d: any) => item.includes(d.operativeId))
          ?.map((d: any) => d.operativeName)
          .slice(0, 3)
          .join(', ')
        : t(`REPORT.FILTER.ALL`)

      await ExportToExcel({
        data: operativeExport,
        branches,
        branchId,
        filter: {
          billStatus,
          operativeTypeName,
          operativeName,
        },
        filterDateStart: rangeDate[0],
        filterDateEnd: rangeDate[1],
        snType: true,
        t,
      })
    } catch (error) {
      console.error('Error exporting to Excel:', error);
    } finally {
      setLoading(false);
    }
  }

  const renderData = (data: any, no: number) => {
    const num = no + 1
    const objRenderData = {
      key: num.toString(),
      id: no.toString(),
      obj: data,
      columns: [
        { option: 'TEXT', align: 'left', label: t('REPORT.TABLE.OPERATIVE_SUMMARY_MONTH.SUM_QTY') },
        { option: 'TEXT', align: 'right', label: data.operativeTypeName, class: 'text-nowrap' },
        { option: 'TEXT', align: 'right', label: data.sumQty, class: 'text-nowrap' },
        { option: 'TEXT', align: 'right', label: data.treatmentsPrice, class: 'text-nowrap' },
        { option: 'TEXT', align: 'right', label: data.sumTotal, class: 'text-nowrap' }
      ]
    }
    return <TableRowCommon {...objRenderData} />
  }

  const renderOperativeExport = (data: any, no: number) => {
    const num = no + 1
    return (
      <>
        <TableRowExport
          key={num.toString()}
          id={no.toString()}
          obj={data}
          columns={[
            { option: 'TEXT', align: 'center', label: num, },
            { option: 'TEXT', align: 'left', label: toBuddhistYear(moment(data.createdAt), 'DD/MM/YYYY HH:mm น.') },
            { option: 'TEXT', align: 'left', label: data.cnNumber, },
            { option: 'TEXT', align: 'left', label: data.branchCnNumber, },
            { option: 'TEXT', align: 'left', label: data.fullName, },
            { option: 'TEXT', align: 'left', label: data.operativeTypeName },
            { option: 'TEXT', align: 'left', label: data.operativeName, },
            { option: 'TEXT', align: 'right', label: numberFormat(data.price) },
            { option: 'TEXT', align: 'right', label: data.qty, },
            { option: 'TEXT', align: 'right', label: numberFormat(data.discount), },
            { option: 'TEXT', align: 'right', label: numberFormat(data.additional), },
            { option: 'TEXT', align: 'right', label: numberFormat(data.total), },
            { option: 'TEXT', align: 'right', label: numberFormat(data.amountPaid) }
          ]}
        />
      </>
    )
  }

  const renderSumOperative = (data: any, no: number) => {
    const num = no + 1
    return (
      <>
        <TableRowCommon
          key={num.toString()}
          id={no.toString()}
          obj={data}
          columns={[
            { option: 'TEXT', align: 'center', label: num },
            { option: 'TEXT', align: 'left', label: data.operativeTypeName },
            { option: 'TEXT', align: 'center', label: data.qty },
            { option: 'TEXT', align: 'right', label: numberFormat(data.discount) },
            { option: 'TEXT', align: 'right', label: numberFormat(data.additional) },
            { option: 'TEXT', align: 'right', label: numberFormat(data.total) },
            { option: 'TEXT', align: 'right', label: numberFormat(data.amountPaid) }
          ]}
        />
      </>
    )
  }
  const renderSumOperativeExport = (data: any, no: number) => {
    const num = no + 1
    return (
      <>
        <TableRowExport
          key={num.toString()}
          id={no.toString()}
          obj={data}
          columns={[
            { option: 'TEXT', align: 'center', label: num },
            { option: 'TEXT', align: 'left', label: data.operativeTypeName },
            { option: 'TEXT', align: 'right', label: data.qty },
            { option: 'TEXT', align: 'center', label: numberFormat(data.discount) },
            { option: 'TEXT', align: 'center', label: numberFormat(data.additional) },
            { option: 'TEXT', align: 'center', label: numberFormat(data.total) },
            { option: 'TEXT', align: 'right', label: numberFormat(data.amountPaid) }
          ]}
        />
      </>
    )
  }
  /** UseEffect */
  useEffect(() => {
    fetchOperative()
  }, [fetchOperative])

  useEffect(() => {
    loadOperativeExport()
  }, [loadOperativeExport])

  const handleChangeSelectItem = (e: any) => setItem(handleChangeFilterSelect(e))

  const { onDownload } = useDownloadExcel({
    tableExportRef: componentRef.current,
    filename: `${t(`REPORT.REPORT_NAME.OPERATIVE_SUMMARY_MONTH`)}`,
    sheetName: `${t(`REPORT.REPORT_NAME.OPERATIVE_SUMMARY_MONTH`)}`
  })

  const headCells = [
    { id: 'message', disablePadding: false, labelKey: 'REPORT.TABLE.OPERATIVE_SUMMARY_MONTH.#NO', align: 'center', class: 'text-nowrap', width: 45 },
    { id: 'message', disablePadding: false, labelKey: 'REPORT.TABLE.OPERATIVE_SUMMARY_MONTH.DATE', align: 'left', class: 'text-nowrap', width: 110 },
    { id: 'message', disablePadding: false, labelKey: 'REPORT.TABLE.OPERATIVE_SUMMARY_MONTH.CN_NUMBER', align: 'left', class: 'text-nowrap', width: 65 },
    { id: 'message', disablePadding: false, labelKey: 'REPORT.TABLE.OPERATIVE_SUMMARY_MONTH.BRANCH_CN_NUMBER', align: 'left', class: 'text-nowrap', width: 85 },
    { id: 'message', disablePadding: false, labelKey: 'REPORT.TABLE.OPERATIVE_SUMMARY_MONTH.FULL_NAME', align: 'left', },
    { id: 'message', disablePadding: false, labelKey: 'REPORT.TABLE.OPERATIVE_SUMMARY_MONTH.OPERATIVE_TYPE', align: 'left', },
    { id: 'message', disablePadding: false, labelKey: 'REPORT.TABLE.OPERATIVE_SUMMARY_MONTH.LIST', align: 'left', },
    { id: 'price', disablePadding: false, labelKey: 'REPORT.TABLE.OPERATIVE_SUMMARY_MONTH.PRICE', align: 'right', class: 'text-nowrap', width: 65 },
    { id: 'message', disablePadding: false, labelKey: 'REPORT.TABLE.OPERATIVE_SUMMARY_MONTH.QTY', align: 'center', class: 'text-nowrap', width: 65 },
    { id: 'price', disablePadding: false, labelKey: 'REPORT.TABLE.OPERATIVE_SUMMARY_MONTH.DISCOUNT', align: 'right', class: 'text-nowrap', width: 65 },
    { id: 'price', disablePadding: false, labelKey: 'REPORT.TABLE.OPERATIVE_SUMMARY_MONTH.ADDITIONAL', align: 'right', class: 'text-nowrap', width: 65 },
    { id: 'price', disablePadding: false, labelKey: 'REPORT.TABLE.OPERATIVE_SUMMARY_MONTH.TOTAL_PRICE', align: 'right', class: 'text-nowrap', width: 65 },
    { id: 'price', disablePadding: false, labelKey: 'REPORT.TABLE.OPERATIVE_SUMMARY_MONTH.TOTAL_PAID', align: 'right', class: 'text-nowrap', width: 65 }
  ]

  const headCellTotal = [
    { id: 'message', disablePadding: false, labelKey: 'TABLE.NO', align: 'left', class: 'text-nowrap' },
    { id: 'message', disablePadding: false, labelKey: 'REPORT.TABLE.OPERATIVE_SUMMARY_MONTH.LIST', align: 'center', class: 'text-nowrap' },
    { id: 'message', disablePadding: false, labelKey: 'REPORT.TABLE.OPERATIVE_SUMMARY_MONTH.QTY', align: 'center', class: 'text-nowrap' },
    { id: 'price', disablePadding: false, labelKey: 'REPORT.TABLE.OPERATIVE_SUMMARY_MONTH.DISCOUNT', align: 'right', class: 'text-nowrap' },
    { id: 'price', disablePadding: false, labelKey: 'REPORT.TABLE.OPERATIVE_SUMMARY_MONTH.ADDITIONAL', align: 'right', class: 'text-nowrap' },
    { id: 'price', disablePadding: false, labelKey: 'REPORT.TABLE.OPERATIVE_SUMMARY_MONTH.TOTAL_PRICE', align: 'right', class: 'text-nowrap' },
    { id: 'price', disablePadding: false, labelKey: 'REPORT.TABLE.OPERATIVE_SUMMARY_MONTH.TOTAL_PAID', align: 'right', class: 'text-nowrap' }
  ]
  const opType = _.find(operativeTypes, (operativeTypesData: any) => operativeTypesData.operativeTypeId === operativeTypeId)
  const setHeaderSearch = (table: string) => {
    const opType = _.find(operativeTypes, (operativeTypesData: any) => operativeTypesData.operativeTypeId === operativeTypeId)
    const opName = _.find(operative, (operativeData: any) => operativeData.operativeId === operativeId)

    const branchName = _.get(_.find(branches, { branchId: branchId }), 'branchName') || _.get(_.find(branches, { branchId: branchId }), 'branchNameEn')
    const operativeTypeName = opType?.operativeTypeName || t(`REPORT.FILTER.ALL`)
    const operativeName = opName?.operativeName || t(`REPORT.FILTER.ALL`)
    const startDate = rangeDate[0] ? toBuddhistYear(moment(rangeDate[0]), 'DD/MM/YYYY') : '-'
    const endDate = rangeDate[1] ? toBuddhistYear(moment(rangeDate[1]), 'DD/MM/YYYY') : '-'

    const data_table_1 = [
      { name: `${t('REPORT.FILTER.BRANCH')}`, style: { width: '1%', whiteSpace: 'nowrap', paddingRight: '2px' } },
      { name: `${branchName}`, style: { width: '1%', whiteSpace: 'nowrap', paddingRight: '8px' } },
      { name: `${t('REPORT.FILTER.RECEIPT_TYPE')}`, style: { width: '1%', whiteSpace: 'nowrap', paddingRight: '2px' } },
      { name: `${billStatus === 'ALL' ? t(`REPORT.FILTER.ALL`) : t(`REPORT.FILTER.BILL.${billStatus}`)}`, style: { width: '1%', whiteSpace: 'nowrap', paddingRight: '8px' } },
      { name: `${t('REPORT.FILTER.OPERATIVE_TYPE')}`, style: { width: '1%', whiteSpace: 'nowrap', paddingRight: '2px' } },
      { name: `${operativeTypeName}`, style: { width: '1%', whiteSpace: 'nowrap', paddingRight: '8px' } },
      { name: `${t('REPORT.FILTER.OPERATIVE')}`, style: { width: '1%', whiteSpace: 'nowrap', paddingRight: '2px' } },
      {
        name:
          item[0] !== 'ALL' ? operative.filter((d: any) => item.includes(d.operativeId))?.map((d: any) => d.operativeName).slice(0, 3).join(', ') : t(`REPORT.FILTER.ALL`),
        style: { width: '1%', whiteSpace: 'nowrap', paddingRight: '8px' }
      }
    ]

    const data_table_2 = [
      { name: `${t('REPORT.FILTER.DATE_START')}`, style: { width: '1%', whiteSpace: 'nowrap', paddingRight: '2px' } },
      { name: `${startDate}`, style: { width: '1%', whiteSpace: 'nowrap', paddingRight: '5px' } },
      { name: `${t('REPORT.FILTER.DATE_END')}`, style: { width: '1%', whiteSpace: 'nowrap', paddingRight: '2px' } },
      { name: `${endDate}`, style: { width: '1%', whiteSpace: 'nowrap' } }
    ]

    switch (table) {
      case 'table_1':
        return data_table_1
      case 'table_2':
        return data_table_2
      default:
        return [...data_table_1, ...data_table_2, { name: ``, style: {} }]
    }
  }

  return (
    <Box sx={{ height: '100%' }}>
      <HeaderCard text={t(`REPORT.REPORT_NAME.OPERATIVE_SUMMARY_MONTH`)} />
      <Box padding="16px">
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3} xl={2}>
            <SelectInput
              label={t('REPORT.FILTER.BRANCH')}
              value={branchId}
              onChange={e => {
                setBranchId(Number(e.target.value));
                setOperativePage(1)
              }}
              options={[
                // { value: 0, name: t(`REPORT.FILTER.ALL`) },
                ...branches.map(b => ({
                  value: b.branchId,
                  name: b.branchName || b.branchNameEn || ''
                }))]}
              width="100%"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3} xl={2}>
            <SelectInput
              label={t('REPORT.FILTER.RECEIPT_STATUS')}
              value={billStatus}
              onChange={e => {
                setBillStatus(e.target.value)
                setOperativePage(1)
              }}
              options={[
                { value: "ALL", name: t(`REPORT.FILTER.ALL`) },
                { value: "NORMAL", name: t(`REPORT.FILTER.BILL.NORMAL`) },
                { value: "CANCEL", name: t(`REPORT.FILTER.BILL.CANCEL`) },
              ]}
              width="100%"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3} xl={2}>
            <Box className="position-relative" sx={{ '.MuiOutlinedInput-input': { paddingLeft: '125px !important' } }}>
              <Typography className="position-absolute top-50 translate-middle-y" sx={{ left: '16px', zIndex: 1 }}>
                {t('REPORT.FILTER.OPERATIVE_TYPE')}:
              </Typography>
              <AutocompleteSelect
                labelId="code-form"
                noOptionsText={t('NOT_FOUND')}
                options={['ALL', ...operativeTypes]}
                getOptionLabel={(option: any) => `${option === "ALL" ? t('REPORT.REPORT_GROUP.ALL') : (option?.operativeTypeName || '')}`}
                renderOption={(props, option: any) => (
                  <Box key={option} component="li" {...props}>
                    {option === "ALL" ? t('REPORT.REPORT_GROUP.ALL') : option.operativeTypeName}
                  </Box>
                )}
                onchange={(e, value: any) => {
                  setOperativeTypeId(value?.operativeTypeId)
                  setOperativePage(1)
                }}
                value={_.find(operativeTypes, (val: any) => Number(val.operativeTypeId) === Number(operativeTypeId)) || "ALL"}
                height={40}
                disabledBorder
              />
            </Box>
          </Grid>

          <Grid item xs={12} sm={6} md={3} xl={2}>
            <SelectCheckBoxInput
              label={t('รายการ')}
              options={[
                ...operative.map((op: any) => ({
                  id: op.operativeId,
                  name: op.operativeName || '',
                })),
              ]}
              selected={item}
              onChange={(ids: (string | number)[]) => {
                setItem(ids);
                setIsOpenListItem(false);
              }}
              fullWidth
              width="100%"
              disabled={operativeTypeId === 'ALL' || operativeTypeId === undefined}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3} xl={2}>
            <InputSecondNewRangePicker
              inputHeight={40}
              value={rangeDate || emptyRangePicker}
              onchange={(val: any) => {
                if (_.isEmpty(val)) setRangeDate(emptyRangePicker)
                else setRangeDate([moment(val[0]).format('YYYY-MM-DD'), moment(val[1]).format('YYYY-MM-DD')])
                setOperativePage(1)
              }}
              label={''}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={9} xl={2}>
            <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
              <MainButton
                title={t('INPUT.SEARCH')}
                onClick={() => {
                  setFetch(true)
                  setOperativePage(1)
                  setLoading(true)
                }}
                type={'primary'}
                variant={'contained'}
                style={{ width: '120px' }}
              />
              <DropdownButton title='พิมพ์เอกสาร' onChange={(e) => {
                if (e === 'Excel') {
                  exportExcel();
                } else if (e === 'PDF') {
                  const style = document.createElement("style");
                  style.innerHTML = `
                      @media print {
                        @page { size: landscape }
                      }
                    `;
                  document.head.appendChild(style);
                  window.print();
                  document.head.removeChild(style);

                }
              }} options={[{ value: 'Excel', name: ' Excel' }, { value: 'PDF', name: ' PDF' }]} />
            </Box>
          </Grid>
        </Grid>

        <Box marginTop="16px">
          <OperativeList
            page={operativePage}
            pageLimit={operativePageLimit}
            rowCount={operativeRowCount}
            sortBy={operativeSortBy}
            sortType={operativeSortType}
            data={operativeData}
            dataExport={operativeExport}
            onRequestSort={onChangeOperativeSort}
            handleChangeRowsPerPage={onChangeOperativeRowPerPage}
            handleChangePage={onChangeOperativePage}
          />
        </Box>
        <Divider className="mx-0 mt-3 mb-4" sx={{ backgroundColor: 'transparent', opacity: 1, borderColor: `${colors.lightGray} !important` }} />
        <Box sx={{ maxWidth: '720px' }}>
          <TableCustom
            page={operativePage}
            pageLimit={operativePageLimit}
            sortBy={operativeSortBy}
            sortType={operativeSortType}
            rowCount={operativeRowCount}
            onSort={onChangeOperativeSort}
            setPageLimit={onChangeOperativeRowPerPage}
            setPage={onChangeOperativePage}
            headCells={headCellTotal}
            rowsData={_.map(sumOperativeExportData, (d: any, index: number) => {
              return renderSumOperative(d, index)
            })}
            hidePagination={true}
            tableMinWidth={720}
            customScroll
          />
        </Box>

        <Box
          id="print-table-container"
          ref={componentRef}
        >
          <Typography fontSize="14px" fontWeight={400}>{t('REPORT.REPORT_NAME.OPERATIVE_SUMMARY_MONTH')}</Typography>
          <Box display='flex' flexDirection="row" alignItems="center" gap="16px">
            <Typography fontSize="10px" fontWeight={400}>{t('REPORT.FILTER.BRANCH')}: {_.get(_.find(branches, { branchId: branchId }), 'branchName') || _.get(_.find(branches, { branchId: branchId }), 'branchNameEn') || 'ทั้งหมด'}</Typography>
            <Typography fontSize="10px" fontWeight={400}>{t('REPORT.FILTER.RECEIPT_TYPE')}: {billStatus === 'ALL' ? 'ทั้งหมด' : billStatus}</Typography>
            <Typography fontSize="10px" fontWeight={400}>{t('REPORT.FILTER.OPERATIVE_TYPE')}: {opType?.operativeTypeName || t(`REPORT.FILTER.ALL`)}</Typography>
            <Typography fontSize="10px" fontWeight={400}>{t('REPORT.FILTER.OPERATIVE')}: {item[0] !== 'ALL' ? operative.filter((d: any) => item.includes(d.operativeId))?.map((d: any) => d.operativeName).slice(0, 3).join(', ') : t(`REPORT.FILTER.ALL`)}</Typography>
            <Typography fontSize="10px" fontWeight={400}>{t('REPORT.FILTER.DATE_START')}: {toBuddhistYear(moment(rangeDate[0]), 'DD/MM/YYYY')}</Typography>
            <Typography fontSize="10px" fontWeight={400}>{t('REPORT.FILTER.DATE_END')}: {toBuddhistYear(moment(rangeDate[1]), 'DD/MM/YYYY')}</Typography>
          </Box>
          <table style={{ width: '100%', border: '1px solid black', borderCollapse: 'collapse', marginTop: '16px' }}>
            <thead>
              <tr style={{ textAlign: 'center', fontSize: '9.5px', fontWeight: 700 }}>
                <th style={{ borderBottom: '1px solid black' }}>{t('REPORT.TABLE.OPERATIVE_SUMMARY_MONTH.#NO')}</th>
                <th style={{ borderBottom: '1px solid black', textAlign: 'center' }}>{t('REPORT.TABLE.OPERATIVE_SUMMARY_MONTH.DATE')}</th>
                <th style={{ borderBottom: '1px solid black', textAlign: 'center' }}>{t('REPORT.TABLE.OPERATIVE_SUMMARY_MONTH.CN_NUMBER')}</th>
                <th style={{ borderBottom: '1px solid black', textAlign: 'center' }}>{t('REPORT.TABLE.OPERATIVE_SUMMARY_MONTH.BRANCH_CN_NUMBER')}</th>
                <th style={{ borderBottom: '1px solid black', textAlign: 'left' }}>{t('REPORT.TABLE.OPERATIVE_SUMMARY_MONTH.FULL_NAME')}</th>
                <th style={{ borderBottom: '1px solid black', textAlign: 'left' }}>{t('REPORT.TABLE.OPERATIVE_SUMMARY_MONTH.OPERATIVE_TYPE')}</th>
                <th style={{ borderBottom: '1px solid black', textAlign: 'left' }}>{t('REPORT.TABLE.OPERATIVE_SUMMARY_MONTH.LIST')}</th>
                <th style={{ borderBottom: '1px solid black', textAlign: 'right' }}>{t('REPORT.TABLE.OPERATIVE_SUMMARY_MONTH.PRICE')}</th>
                <th style={{ borderBottom: '1px solid black', textAlign: 'right' }}>{t('REPORT.TABLE.OPERATIVE_SUMMARY_MONTH.QTY')}</th>
                <th style={{ borderBottom: '1px solid black', textAlign: 'right' }}>{t('REPORT.TABLE.OPERATIVE_SUMMARY_MONTH.DISCOUNT')}</th>
                <th style={{ borderBottom: '1px solid black', textAlign: 'right' }}>{t('REPORT.TABLE.OPERATIVE_SUMMARY_MONTH.ADDITIONAL')}</th>
                <th style={{ borderBottom: '1px solid black', textAlign: 'right' }}>{t('REPORT.TABLE.OPERATIVE_SUMMARY_MONTH.TOTAL_PRICE')}</th>
                <th style={{ borderBottom: '1px solid black', textAlign: 'right' }}>{t('REPORT.TABLE.OPERATIVE_SUMMARY_MONTH.TOTAL_PAID')}</th>
              </tr>
            </thead>
            <tbody>
              {operativeExport?.length === 0 &&
                <tr>
                  <td colSpan={13} style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'center', verticalAlign: 'center', width: '100%' }}>{t('NOT_FOUND')}</td>
                </tr>
              }
              {operativeExport?.map((data: any, index: number) => (
                <tr key={index}>
                  <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'center', verticalAlign: 'top', width: '50px' }}>{index + 1}</td>
                  <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'center', verticalAlign: 'top', width: '100px' }}>{toBuddhistYear(moment(data.createdAt), 'DD/MM/YYYY HH:mm น.')}</td>
                  <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'center', verticalAlign: 'top', width: '85px' }}>{data?.cnNumber}</td>
                  <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'center', verticalAlign: 'top', width: '85px' }}>{data?.branchCnNumber}</td>
                  <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'left', verticalAlign: 'top' }}>{data?.fullName}</td>
                  <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'left', verticalAlign: 'top' }}>{data?.operativeTypeName}</td>
                  <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'left', verticalAlign: 'top' }}>{data?.operativeName}</td>
                  <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'right', verticalAlign: 'top', width: '85px' }}>{numberFormat(data.price)}</td>
                  <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'right', verticalAlign: 'top', width: '85px' }}>{data?.qty}</td>
                  <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'right', verticalAlign: 'top', width: '85px' }}>{numberFormat(data.discount)}</td>
                  <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'right', verticalAlign: 'top', width: '85px' }}>{numberFormat(data.additional)}</td>
                  <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'right', verticalAlign: 'top', width: '85px' }}>{numberFormat(data.total)}</td>
                  <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'right', verticalAlign: 'top', width: '85px' }}>{numberFormat(data.amountPaid)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <br />

          <table style={{ width: '50%', border: '1px solid black', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ textAlign: 'center', fontSize: '9.5px', fontWeight: 700 }}>
                <th style={{ borderBottom: '1px solid black' }}>{t('REPORT.TABLE.OPERATIVE_SUMMARY_MONTH.#NO')}</th>
                <th style={{ borderBottom: '1px solid black', textAlign: 'left' }}>{t('REPORT.TABLE.OPERATIVE_SUMMARY_MONTH.LIST')}</th>
                <th style={{ borderBottom: '1px solid black', textAlign: 'right' }}>{t('REPORT.TABLE.OPERATIVE_SUMMARY_MONTH.QTY')}</th>
                <th style={{ borderBottom: '1px solid black', textAlign: 'right' }}>{t('REPORT.TABLE.OPERATIVE_SUMMARY_MONTH.DISCOUNT')}</th>
                <th style={{ borderBottom: '1px solid black', textAlign: 'right' }}>{t('REPORT.TABLE.OPERATIVE_SUMMARY_MONTH.ADDITIONAL')}</th>
                <th style={{ borderBottom: '1px solid black', textAlign: 'right' }}>{t('REPORT.TABLE.OPERATIVE_SUMMARY_MONTH.TOTAL_PRICE')}</th>
                <th style={{ borderBottom: '1px solid black', textAlign: 'right' }}>{t('REPORT.TABLE.OPERATIVE_SUMMARY_MONTH.TOTAL_PAID')}</th>
              </tr>
            </thead>
            <tbody>
              {sumOperativeExportData?.map((data: any, index: number) => (
                <tr key={index}>
                  <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'center', verticalAlign: 'top', width: '50px' }}>{index + 1}</td>
                  <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'left', verticalAlign: 'top' }}>{data?.operativeTypeName}</td>
                  <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'right', verticalAlign: 'top', width: '85px' }}>{data?.qty}</td>
                  <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'right', verticalAlign: 'top', width: '85px' }}>{numberFormat(data.discount)}</td>
                  <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'right', verticalAlign: 'top', width: '85px' }}>{numberFormat(data.additional)}</td>
                  <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'right', verticalAlign: 'top', width: '85px' }}>{numberFormat(data.total)}</td>
                  <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'right', verticalAlign: 'top', width: '85px' }}>{numberFormat(data.amountPaid)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Box>
      </Box>
      <Loading show={loading} type="fullLoading" />
    </Box>
  )
}

export function ExportData(props: ExportProps) {
  return (
    <>
      <div className="large" style={{ fontSize: 10, fontWeight: 500 }}>
        {props.headCells}
      </div>
      {props.componant}
    </>
  )
}

export function ExportDataComponent(props: any) {
  const { t } = useTranslation()
  return (
    <>
      <UseStyled.Styles className="pt-3">
        <table style={{ tableLayout: 'fixed', borderCollapse: 'collapse', width: '100%' }}>
          <thead>
            <tr>
              {props.headCells.map((val: any, index: number) => (
                <th key={index} style={{ textAlign: val.align, fontSize: 10, fontWeight: 500, ...val.width && { width: val.width } }}>
                  {t(val.labelKey)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {props.operativeExport.map((d: any, index: number) => props.renderOperativeExport(d, index))}
            <tr>
              <td align="right" colSpan={7} style={{ fontSize: 10 }}>
                {t('REPORT.TITLE.SUM')}
              </td>

              <td align="right" colSpan={2} style={{ fontSize: 10, textAlign: 'right' }}>
                {numberFormat(_.sumBy(props.operativeExport, 'qty'), 0)}
              </td>
              <td align="right" colSpan={1} style={{ fontSize: 10, textAlign: 'right' }}>
                {numberFormat(_.sumBy(props.operativeExport, 'discount'))}
              </td>
              <td align="right" colSpan={1} style={{ fontSize: 10, textAlign: 'right' }}>
                {numberFormat(_.sumBy(props.operativeExport, 'additional'))}
              </td>
              <td align="right" colSpan={1} style={{ fontSize: 10, textAlign: 'right' }}>
                {numberFormat(_.sumBy(props.operativeExport, 'total'))}
              </td>
              <td align="right" colSpan={1} style={{ fontSize: 10, textAlign: 'right' }}>
                {numberFormat(_.sumBy(props.operativeExport, 'amountPaid'))}
              </td>
            </tr>
          </tbody>
        </table>
      </UseStyled.Styles >

      <br></br>
      <UseStyled.Styles className="pt-3">
        <table style={{ tableLayout: 'fixed', borderCollapse: 'collapse', minWidth: '100%', maxWidth: '100%' }}>
          <thead>
            <tr>
              {_.map(props.headCellTotal, (val: any, index: number) => {
                return <td style={{ textAlign: val.align, fontSize: 10, fontWeight: 500 }}> {t(val.labelKey)}</td>
              })}
            </tr>
          </thead>
          <tbody>
            {_.map(props.sumOperativeExportData, (d: any, index: number) => {
              return props.renderSumOperativeExport(d, index)
            })}
          </tbody>
        </table>
      </UseStyled.Styles>
    </>
  )
}
